import { useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import WizardStep from '../Shared/WizardStep';
import { WizardStepState } from '../Shared/WizardStep/models/wizard-step-state.enum';
import HoneyPot, { type IHoneyPot } from '../../components/Shared/HoneyPot';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../Shared/TextFieldFormGroup';
import { SanitizerService } from '../../services/sanitizer.service';
import { TranslationService } from '../../services/translation.service';
import SubmitFailed from '../Shared/SubmitFailed';
import SubmitSuccessful from '../Shared/SubmitSuccessful';
import ValidationInfo from '../../validation/validation-info';
import type IValidatable from '../Shared/validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../helpers/validation.helper';
import ValidationSummary from '../Shared/ValidationSummary';
import type ITravelAssistanceCardApplication from './models/travel-assistance-card-application.model';
import Consent, { type IConsentState } from '../Shared/Consent';
import { LanguageService } from '../../services/language.service';
import { AnalyticsService } from '../../services/analytics-service';
import type { IServerValidationError } from '../Shared/validatable.interface';
import FileUpload, { type IFileUploadState } from '../Shared/FileUpload';
import { Status } from '../../hooks/fetch.hook';
import { DomHelper } from '../../helpers/dom.helper';
import AntiforgeryService from '../../services/anti-forgery.service';

function TravelAssistanceCardForm() {
  // === STATE ===
  const [firstNameState, setFirstNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [lastNameState, setLastNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [emailState, setEmailState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [personalIdentityNumberState, setPersonalIdentityNumberState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [careOfState, setCareOfState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [streetAddressState, setStreetAddressState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [postalCodeState, setPostalCodeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [cityState, setCityState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [fileUploadState, setFileUploadState] = useState<IFileUploadState>({ value: [], validationInfo: new ValidationInfo() });
  const [consentState, setConsentState] = useState<IConsentState>({ value: false, validationInfo: new ValidationInfo() });
  const [caseId, setCaseId] = useState('');

  const [submitStatus, setSubmitStatus] = useState(Status.notperformed);
  const [submitErrorCode, setSubmitErrorCode] = useState<number | undefined>(undefined);

  // === REFS ===
  const firstNameRef = useRef<IValidatable>(null);
  const lastNameRef = useRef<IValidatable>(null);
  const emailRef = useRef<IValidatable>(null);
  const personalIdentityNumberRef = useRef<IValidatable>(null);
  const careOfRef = useRef<IValidatable>(null);
  const streetAddressRef = useRef<IValidatable>(null);
  const postalCodeRef = useRef<IValidatable>(null);
  const cityRef = useRef<IValidatable>(null);
  const fileUploadRef = useRef<IValidatable>(null);
  const consentRef = useRef<IValidatable>(null);
  const honeyPotRef = useRef<IHoneyPot>(null);

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: firstNameRef.current!, validationInfo: firstNameState.validationInfo, setState: setFirstNameState },
      { component: lastNameRef.current!, validationInfo: lastNameState.validationInfo, setState: setLastNameState },
      { component: emailRef.current!, validationInfo: emailState.validationInfo, setState: setEmailState },
      { component: personalIdentityNumberRef.current!, validationInfo: personalIdentityNumberState.validationInfo, setState: setPersonalIdentityNumberState },
      { component: careOfRef.current!, validationInfo: careOfState.validationInfo, setState: setCareOfState },
      { component: streetAddressRef.current!, validationInfo: streetAddressState.validationInfo, setState: setStreetAddressState },
      { component: postalCodeRef.current!, validationInfo: postalCodeState.validationInfo, setState: setPostalCodeState },
      { component: cityRef.current!, validationInfo: cityState.validationInfo, setState: setCityState },
      { component: fileUploadRef.current!, validationInfo: fileUploadState.validationInfo, setState: setFileUploadState },
      { component: consentRef.current!, validationInfo: consentState.validationInfo, setState: setConsentState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.TravelAssistanceCard, AnalyticsService.ErrorAction, `Ansök om reshjälpskort - ${e}`);
    });
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === EVENT HANDLERS ===
  async function handleSubmitClick() {
    let isFormValid: boolean = false;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (submitStatus === Status.loading) return;

      setSubmitStatus(Status.notperformed);
      setSubmitErrorCode(undefined);

      if (!honeyPotRef.current!.isValid()) return;

      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.TravelAssistanceCard, AnalyticsService.ErrorAction, e);
        });
        return;
      }

      setSubmitStatus(Status.loading);
      const travelAssistanceCardApplication: ITravelAssistanceCardApplication = {
        firstName: firstNameState.value,
        lastName: lastNameState.value,
        email: emailState.value,
        personalIdentityNumber: personalIdentityNumberState.value,
        careOf: careOfState.value,
        streetAddress: streetAddressState.value,
        postalCode: postalCodeState.value.replace(/\s/g, ''),
        city: cityState.value,
        isConsentGiven: consentState.value,
      };

      const formData = new FormData();
      formData.append('travelAssistanceCardInfo', JSON.stringify(travelAssistanceCardApplication));
      fileUploadState.value.forEach((file, index) => {
        formData.append(`files[${index}]`, file.actualFile);
      });

      try {
        const response = await fetch(`/api/travelassistancecard?language=${LanguageService.getCurrentLanguage()}`, {
          method: 'POST',
          body: formData,
          headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        });
        if (response.ok) {
          setCaseId(await response.text());
          setSubmitStatus(Status.success);
          submitResult = { status: Status.success, errorCode: undefined };
          AnalyticsService.addEvent(AnalyticsService.TravelAssistanceCard, 'Skickat', '');
        } else {
          if (response.status === 400) {
            const responseBody = await response.json();
            if (!responseBody.errors) throw new Error('Missing errors');
            handleServerValidationErrors(responseBody.errors);
          }
          setSubmitStatus(Status.failed);
          setSubmitErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          AnalyticsService.addEvent(AnalyticsService.TravelAssistanceCard, 'Skickat - misslyckat', '');
        }
      } catch {
        setSubmitStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: undefined };
      }
    });
    if (!isFormValid || submitResult?.errorCode === 400) {
      setTimeout(() => {
        document.getElementById('travel-assistance-card-form')?.scrollIntoView(true);
        document.getElementById('travel-assistance-card-form-validation-summary')?.focus();
      }, 100);
    } else {
      setTimeout(() => {
        DomHelper.scrollAndFocus('travel-assistance-card-form-submit-result');
      }, 100);
    }
  }

  return (
    <div>
      <>
        {submitStatus !== Status.success && (submitStatus !== Status.failed || submitErrorCode === 400) && (
          <>
            {/* === WizardStep 1 === */}
            <WizardStep
              id="travel-assistance-card-form-step-1"
              number={1}
              state={WizardStepState.general}
              heading={TranslationService.get('travelassistancecardformpage.steponeheading')}
              className="mb-3 mb-md-4"
            >
              <div className="m-3">
                {TranslationService.get('common.formsrequiredfieldsmessage') && <p className="mb-3">{TranslationService.get('common.formsrequiredfieldsmessage')}</p>}
                <ValidationSummary
                  id="travel-assistance-card-form-validation-summary"
                  validationSummaryErrors={validationSummaryErrors}
                  className={validationSummaryErrors.length > 0 ? 'mb-3 mb-md-4' : ''}
                />
                {TranslationService.get('travelassistancecardformpage.steponeinformationtext') && (
                  <div className="mb-3" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('travelassistancecardformpage.steponeinformationtext')) }}></div>
                )}
                <TextFieldFormGroup
                  ref={personalIdentityNumberRef}
                  className={TranslationService.get('travelassistancecardformpage.StepOnePersonalNumberInformationText') ? 'mb-1' : ''}
                  labelText={TranslationService.get('common.personalidentitynumber')}
                  required={true}
                  inputId="personal-identity-number"
                  serverValidationName="personalIdentityNumber"
                  autocomplete="none"
                  state={personalIdentityNumberState}
                  onChange={(value) => {
                    setPersonalIdentityNumberState({ ...personalIdentityNumberState, value });
                  }}
                  onKeyUp={() => {
                    setPersonalIdentityNumberState({ ...personalIdentityNumberState, validationInfo: new ValidationInfo() });
                  }}
                />
                {TranslationService.get('travelassistancecardformpage.StepOnePersonalNumberInformationText') && (
                  <div
                    className="mb-3"
                    dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('travelassistancecardformpage.StepOnePersonalNumberInformationText')) }}
                  ></div>
                )}
                <TextFieldFormGroup
                  ref={firstNameRef}
                  labelText={TranslationService.get('common.firstname')}
                  required={true}
                  inputId="first-name"
                  serverValidationName="firstName"
                  autocomplete="given-name"
                  state={firstNameState}
                  onChange={(value) => {
                    setFirstNameState({ ...firstNameState, value });
                  }}
                  onKeyUp={() => {
                    setFirstNameState({ ...firstNameState, validationInfo: new ValidationInfo() });
                  }}
                />
                <TextFieldFormGroup
                  ref={lastNameRef}
                  labelText={TranslationService.get('common.lastname')}
                  required={true}
                  inputId="last-name"
                  serverValidationName="lastName"
                  autocomplete="family-name"
                  state={lastNameState}
                  onChange={(value) => {
                    setLastNameState({ ...lastNameState, value });
                  }}
                  onKeyUp={() => {
                    setLastNameState({ ...lastNameState, validationInfo: new ValidationInfo() });
                  }}
                />
                <TextFieldFormGroup
                  ref={careOfRef}
                  labelText={TranslationService.get('travelassistancecardformpage.careof')}
                  required={false}
                  inputId="care-of"
                  serverValidationName="careOf"
                  autocomplete="none"
                  state={careOfState}
                  onChange={(value) => {
                    setCareOfState({ ...careOfState, value });
                  }}
                  onKeyUp={() => {
                    setCareOfState({ ...careOfState, validationInfo: new ValidationInfo() });
                  }}
                />
                <TextFieldFormGroup
                  ref={streetAddressRef}
                  labelText={TranslationService.get('travelassistancecardformpage.streetaddress')}
                  required={true}
                  inputId="street-address"
                  serverValidationName="streetAddress"
                  autocomplete="address-line1"
                  state={streetAddressState}
                  onChange={(value) => {
                    setStreetAddressState({ ...streetAddressState, value });
                  }}
                  onKeyUp={() => {
                    setStreetAddressState({ ...streetAddressState, validationInfo: new ValidationInfo() });
                  }}
                />
                <TextFieldFormGroup
                  ref={postalCodeRef}
                  labelText={TranslationService.get('travelassistancecardformpage.postalcode')}
                  required={true}
                  inputId="postal-code"
                  serverValidationName="postalCode"
                  autocomplete="postal-code"
                  state={postalCodeState}
                  onChange={(value) => {
                    setPostalCodeState({ ...postalCodeState, value });
                  }}
                  onKeyUp={() => {
                    setPostalCodeState({ ...postalCodeState, validationInfo: new ValidationInfo() });
                  }}
                  maxLength={6}
                />
                <TextFieldFormGroup
                  ref={cityRef}
                  labelText={TranslationService.get('travelassistancecardformpage.city')}
                  required={true}
                  inputId="city"
                  serverValidationName="city"
                  autocomplete="none"
                  state={cityState}
                  onChange={(value) => {
                    setCityState({ ...cityState, value });
                  }}
                  onKeyUp={() => {
                    setCityState({ ...cityState, validationInfo: new ValidationInfo() });
                  }}
                />
                <TextFieldFormGroup
                  ref={emailRef}
                  labelText={TranslationService.get('common.email')}
                  required={false}
                  inputId="email"
                  serverValidationName="email"
                  autocomplete="email"
                  state={emailState}
                  onChange={(value) => {
                    setEmailState({ ...emailState, value });
                  }}
                  onKeyUp={() => {
                    setEmailState({ ...emailState, validationInfo: new ValidationInfo() });
                  }}
                />
              </div>
            </WizardStep>

            {/* === WizardStep 2 === */}
            <WizardStep
              id="travel-assistance-card-form-step-2"
              number={2}
              state={WizardStepState.general}
              heading={TranslationService.get('travelassistancecardformpage.steptwoheading')}
              className="mb-3 mb-md-4"
            >
              <div className="m-3">
                {TranslationService.get('travelassistancecardformpage.steptwoinformationtext') && (
                  <div className="mb-3" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('travelassistancecardformpage.steptwoinformationtext')) }}></div>
                )}
                <FileUpload
                  id="travel-assistance-card-file-upload"
                  ref={fileUploadRef}
                  isRequired={true}
                  state={fileUploadState}
                  onFilesAdded={(files) => {
                    setFileUploadState((ps) => ({ ...ps, value: [...ps.value, ...files] }));
                  }}
                  onFileRemoved={(file) => {
                    setFileUploadState({ ...fileUploadState, value: fileUploadState.value.filter((f) => f !== file), validationInfo: new ValidationInfo() });
                  }}
                />
                <hr className="my-4" />
                <Consent
                  id="travel-assistance-card-consent"
                  ref={consentRef}
                  consentText={TranslationService.get('travelassistancecardformpage.consenttext')}
                  integrityPolicyInfo={TranslationService.get('travelassistancecardformpage.integritypolicyinfo')}
                  termsAndConditions={TranslationService.get('travelassistancecardformpage.termsandconditions.heading')}
                  state={consentState}
                  className="travelassistance-card"
                  onChange={(value) => {
                    setConsentState({ value, validationInfo: new ValidationInfo() });
                  }}
                />
              </div>
            </WizardStep>

            <HoneyPot ref={honeyPotRef} />
            <div className="d-grid">
              <button className={`btn btn-primary ${submitStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={handleSubmitClick} type="button">
                <span hidden={submitStatus === Status.loading}>{TranslationService.get('common.send')}</span>
              </button>
            </div>
          </>
        )}
      </>

      <div id="travel-assistance-card-form-submit-result" role="alert" aria-live="polite">
        {submitStatus === Status.failed && submitErrorCode !== 400 && submitErrorCode !== 409 && (
          <>
            <SubmitFailed failedText={TranslationService.get('travelassistancecardformpage.generalerrortext')} />
            <button
              type="button"
              className="btn btn-primary d-block mx-auto"
              onClick={() => {
                setSubmitStatus(Status.notperformed);
              }}
            >
              {TranslationService.get('travelassistancecardformpage.dismissgeneralerror')}
            </button>
          </>
        )}
        {submitStatus === Status.success && <SubmitSuccessful successText={TranslationService.get('travelassistancecardformpage.completedtext').replace('{caseId}', caseId)} />}
      </div>
    </div>
  );
}

export default TravelAssistanceCardForm;
